import {FC, PropsWithChildren} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {useHeaderContent} from '@/content/header'
import {useTranslation} from '@/i18n'
import {theme} from '@/theme/theme'
import {useNavigation} from '@/utils/hooks/useNavigation'
import {ROUTE_TO_PAGENAME} from '@/utils/pageNames'
import {TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'

import {QuestionMarkOutlineIcon} from '../common/icons/QuestionMarkOutlineIcon'
import {useHelpCenterContext} from './context/HelpCenterContext'

type HelpCenterButtonProps = {
  onClick?: () => void
}

type HelpButtonProps = HelpCenterButtonProps & {
  hasBorder?: boolean
  iconOnly?: boolean
}

const HelpButton: FC<HelpButtonProps> = ({onClick, hasBorder = false, iconOnly = false}) => {
  const {t} = useTranslation('page')
  const router = useNavigation()
  const route = router.route
  const pageName = ROUTE_TO_PAGENAME[route] || route

  const {getHelpButtonText} = useHeaderContent()
  const {setIsActive} = useHelpCenterContext()

  const handleClickOnGetHelp = () => {
    setIsActive(true)
    analytics.track(`Get help - Page: ${pageName}`)
    onClick?.()
  }

  return (
    <Button
      size={ButtonSize.medium}
      variant={ButtonVariant.darkGray}
      type="button"
      onClick={handleClickOnGetHelp}
      icon={<QuestionMarkOutlineIcon color={theme.palette.text.white} />}
      iconPosition="left"
      hasBorder={hasBorder}
      fullWidth
      // Icon-only in the desktop header, so the label has to come from aria-label instead
      ariaLabel={iconOnly ? t(getHelpButtonText) : undefined}
    >
      {!iconOnly && t(getHelpButtonText)}
    </Button>
  )
}

export const HelpCenterButton: FC<PropsWithChildren<HelpCenterButtonProps>> = ({onClick}) => {
  const {requestLoad} = useHelpCenterContext()

  // pointerenter fires on touch start too, so taps also get a head start
  return (
    <>
      <div className="desktop" onPointerEnter={requestLoad} onFocus={requestLoad}>
        <HelpButton onClick={onClick} iconOnly />
      </div>
      <div className="mobile" onPointerEnter={requestLoad} onFocus={requestLoad}>
        <HelpButton onClick={onClick} hasBorder />
      </div>

      <style jsx>{`
        .mobile {
          display: none;
        }
        @media ${TABLET_SCREENSIZE} {
          .desktop {
            display: none;
          }
          .mobile {
            display: block;
          }
        }
      `}</style>
    </>
  )
}
